import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Building2, Loader2 } from 'lucide-react';
import { auth, publicApi, ApiError, RateLimitError, type InvitePageData } from '../lib/apiClient';
import { passwordPasses } from '../lib/passwordPolicy';
import { useAuth } from '../context/AuthContext';
import { AuthCard } from '../components/auth/AuthCard';
import { ErrorBanner } from '../components/auth/ErrorBanner';
import { PasswordField } from '../components/auth/PasswordField';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { DeclarationFields, type DeclarationAnswer } from '../components/profile/DeclarationFields';
import { usePageTitle } from '../lib/usePageTitle';
import { rememberInviteToken } from '../lib/inviteToken';

/**
 * Recruitment workflow §6.3 — the invite page a candidate reaches from the "invited to apply"
 * email (`/invite/:token`). The token is the only credential; the page is never linked from
 * anywhere public.
 *
 * - **Client identity:** the company card here (and the email) is the only place a candidate
 *   sees the client company. The public job page it links to stays anonymous (§6.2).
 * - **Account state** decides the action: already signed in as the invitee → continue; signed in
 *   as someone else → sign out first; has a password → sign in; Google only → sign in with
 *   Google; unclaimed (an account QuantumHire created from their resume) → the claim form.
 * - The claim form is offered even when the job is no longer available (decided step 5c).
 * - For a live invite the token is kept in sessionStorage for the job, so the apply that follows
 *   can be attributed to the invite (step 5d).
 * - The token is masked in analytics and Sentry (`lib/maskTokenUrl.ts`) and the page is noindex.
 */
export function InvitePage() {
  usePageTitle('QuantumHire - Your invitation');
  useNoIndex();
  const { token = '' } = useParams<{ token: string }>();
  const { candidate, isLoading: authLoading, setCandidate } = useAuth();

  const [data, setData] = useState<InvitePageData | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadVersion, setReloadVersion] = useState(0);
  const lastKey = useRef<string | null>(null);

  // StrictMode-safe mount fetch: the key is pinned only after the request resolves.
  useEffect(() => {
    const key = `${token}|${reloadVersion}`;
    if (lastKey.current === key) return;
    let cancelled = false;
    setLoadError(null);
    publicApi
      .invite(token)
      .then((d) => {
        if (cancelled) return;
        lastKey.current = key;
        setData(d);
        setInvalid(false);
        if (d.state === 'available') rememberInviteToken(d.job.id, token);
      })
      .catch((err) => {
        if (cancelled) return;
        lastKey.current = key;
        if (err instanceof ApiError && err.status === 404) {
          setInvalid(true);
        } else {
          setLoadError(
            err instanceof ApiError ? err.message : 'Could not load your invitation. Check your connection and try again.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, reloadVersion]);

  const reload = useCallback(() => setReloadVersion((v) => v + 1), []);

  if (invalid || !token) {
    return (
      <AuthCard publicTheme eyebrow="Your invitation" title="This invite link isn't valid">
        <p className="text-brand-muted mb-6">
          If you received a newer invite, use the link in the latest email. You can also browse open roles or
          sign in to your account.
        </p>
        <div className="flex flex-col gap-3">
          <PrimaryLink to="/job-seekers">Browse jobs</PrimaryLink>
          <SecondaryLink to="/signin">Sign in</SecondaryLink>
        </div>
      </AuthCard>
    );
  }

  if (loadError) {
    return (
      <AuthCard publicTheme eyebrow="Your invitation" title="Something went wrong">
        <ErrorBanner message={loadError} />
        <button type="button" onClick={reload} className={PRIMARY_BUTTON}>
          Try again
        </button>
      </AuthCard>
    );
  }

  if (!data) {
    return (
      <AuthCard publicTheme eyebrow="Your invitation" title="Loading your invitation…">
        <div className="flex items-center gap-2 text-brand-muted">
          <Loader2 className="w-4 h-4 animate-spin" />
          Just a moment.
        </div>
      </AuthCard>
    );
  }

  const jobPath = `/job-seekers/${data.job.slug}`;
  const firstName = data.invitee.full_name.split(' ')[0] || data.invitee.full_name;

  if (data.state === 'already_applied') {
    return (
      <AuthCard publicTheme eyebrow="Your invitation" title="You've already applied">
        <p className="text-brand-muted mb-6">
          You applied for <span className="font-bold text-brand-primary">{data.job.title}</span>. You can follow
          its progress in My Applications.
        </p>
        <PrimaryLink to="/dashboard/applications">View my applications</PrimaryLink>
      </AuthCard>
    );
  }

  const available = data.state === 'available';
  const signedInAsInvitee =
    candidate !== null && candidate.email.toLowerCase() === data.invitee.email.toLowerCase();

  return (
    <AuthCard
      publicTheme
      eyebrow="Your invitation"
      title={available ? `${firstName}, you're invited to apply` : 'This job is no longer available'}
      subtitle={
        available
          ? `${data.job.title} · ${data.company?.name ?? ''} · ${data.job.region_name}`
          : `The ${data.job.title} role has closed or is no longer listed.`
      }
    >
      {available && data.company && (
        <>
          <CompanyCard company={data.company} />
          <Link
            to={jobPath}
            className="mt-4 mb-8 inline-flex items-center gap-1.5 text-sm font-bold text-brand-primary hover:text-brand-accent"
          >
            View the job description <ArrowRight className="w-4 h-4" />
          </Link>
        </>
      )}

      {authLoading ? (
        <div className="flex items-center gap-2 text-brand-muted">
          <Loader2 className="w-4 h-4 animate-spin" />
          Checking your sign-in…
        </div>
      ) : signedInAsInvitee ? (
        available ? (
          <PrimaryLink to={jobPath}>Continue to the job</PrimaryLink>
        ) : (
          <PrimaryLink to="/job-seekers">Browse other jobs</PrimaryLink>
        )
      ) : candidate ? (
        <SignedInAsSomeoneElse
          signedInEmail={candidate.email}
          inviteeEmail={data.invitee.email}
          onSignedOut={() => setCandidate(null)}
        />
      ) : data.invitee.account_state === 'unclaimed' ? (
        <ClaimForm
          token={token}
          data={data}
          onClaimedElsewhere={reload}
          destination={available ? jobPath : '/job-seekers'}
        />
      ) : data.invitee.account_state === 'google' ? (
        <GoogleSignIn email={data.invitee.email} destination={available ? jobPath : '/job-seekers'} />
      ) : (
        <PasswordSignIn email={data.invitee.email} destination={available ? jobPath : '/job-seekers'} />
      )}
    </AuthCard>
  );
}

// ------------------------------------------------------------------------------------------------
// Account branches
// ------------------------------------------------------------------------------------------------

function PasswordSignIn({ email, destination }: { email: string; destination: string }) {
  const navigate = useNavigate();
  return (
    <div>
      <p className="text-sm text-brand-muted mb-4">
        Sign in as <span className="font-bold text-brand-primary">{email}</span> to apply.
      </p>
      <button
        type="button"
        className={PRIMARY_BUTTON}
        // The email goes in router state, never the URL — page views are sent to analytics.
        onClick={() => navigate(`/signin?next=${encodeURIComponent(destination)}`, { state: { email } })}
      >
        Sign in to apply <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function GoogleSignIn({ email, destination }: { email: string; destination: string }) {
  const navigate = useNavigate();
  const { setCandidate } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const handleGoogle = useCallback(
    async (idToken: string) => {
      setError(null);
      try {
        const me = await auth.signinWithGoogle({ id_token: idToken });
        setCandidate(me);
        navigate(destination);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not sign you in with Google. Please try again.');
      }
    },
    [navigate, setCandidate, destination],
  );

  return (
    <div>
      <p className="text-sm text-brand-muted mb-4">
        Your account <span className="font-bold text-brand-primary">{email}</span> uses Google sign-in.
      </p>
      <ErrorBanner message={error} />
      <GoogleSignInButton onCredential={handleGoogle} text="signin_with" />
    </div>
  );
}

function SignedInAsSomeoneElse({
  signedInEmail,
  inviteeEmail,
  onSignedOut,
}: {
  signedInEmail: string;
  inviteeEmail: string;
  onSignedOut: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <div>
      <p className="text-sm text-brand-muted mb-4">
        You&apos;re signed in as <span className="font-bold text-brand-primary">{signedInEmail}</span>, but this
        invitation is for <span className="font-bold text-brand-primary">{inviteeEmail}</span>. Sign out to
        continue with the invited account.
      </p>
      <button
        type="button"
        disabled={busy}
        className={PRIMARY_BUTTON}
        onClick={async () => {
          setBusy(true);
          try {
            await auth.signout();
          } catch {
            // Best effort — clear local state either way, as the header's sign-out does.
          }
          onSignedOut();
          setBusy(false);
        }}
      >
        {busy ? 'Signing out…' : 'Sign out'}
      </button>
    </div>
  );
}

/** The unclaimed-account branch: first password + declarations → signed in. */
function ClaimForm({
  token,
  data,
  destination,
  onClaimedElsewhere,
}: {
  token: string;
  data: InvitePageData;
  destination: string;
  onClaimedElsewhere: () => void;
}) {
  const navigate = useNavigate();
  const { setCandidate } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [rtr, setRtr] = useState<DeclarationAnswer>('');
  const [fraud, setFraud] = useState<DeclarationAnswer>('');
  const [fraudError, setFraudError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);

  const passwordsMatch = password.length > 0 && password === confirm;
  const canSubmit = !submitting && passwordPasses(password) && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (fraud !== 'yes') {
      setFraudError('Please agree to the Fraud Declaration to set up your account.');
      document.getElementById('invite-fraud-declaration')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const me = await auth.claimInvite(token, {
        password,
        fraud_declaration_consent: true,
        ...(rtr === '' ? {} : { rtr_consent: rtr === 'yes' }),
      });
      setCandidate(me);
      navigate(destination);
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitedUntil(Date.now() + err.retryAfterSeconds * 1000);
        setError('Too many attempts. Please wait a moment and try again.');
      } else if (err instanceof ApiError && err.code === 'INVALID_STATE_TRANSITION') {
        // Set up meanwhile (another tab, forgot-password, Google) — reload to show sign-in.
        setError(err.message);
        onClaimedElsewhere();
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not set up your account. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const limited = rateLimitedUntil !== null && rateLimitedUntil > Date.now();

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="rounded-2xl bg-gray-50 border border-gray-100 p-4 text-sm text-brand-primary">
        {data.state === 'available'
          ? 'QuantumHire created a profile for you from your resume. '
          : 'The job has closed, but QuantumHire created a profile for you from your resume and you can still set up your account and browse other roles. '}
        Choose a password to claim it — this also confirms your email address{' '}
        <span className="font-bold break-all">{data.invitee.email}</span>.
      </div>

      <ErrorBanner message={error} />

      <PasswordField
        value={password}
        onChange={setPassword}
        showPolicy
        autoComplete="new-password"
        placeholder="Password"
        label="Password"
      />
      <PasswordField
        value={confirm}
        onChange={setConfirm}
        autoComplete="new-password"
        placeholder="Confirm password"
        label="Confirm password"
      />
      {confirm && !passwordsMatch && (
        <p className="text-xs text-red-700 font-medium -mt-2">Passwords don&apos;t match.</p>
      )}

      <div className="pt-2">
        <p className="text-xs font-black uppercase tracking-widest text-brand-muted mb-3">Declaration</p>
        <DeclarationFields
          rtr={rtr}
          fraud={fraud}
          onRtrChange={setRtr}
          onFraudChange={(v) => {
            setFraud(v);
            setFraudError(null);
          }}
          fraudWrapperId="invite-fraud-declaration"
          fraudError={fraudError}
          namePrefix="invite_"
          disabled={submitting}
        />
      </div>

      <button type="submit" disabled={!canSubmit || limited} className={PRIMARY_BUTTON}>
        {submitting ? 'Setting up…' : 'Set up my account'}
        {!submitting && <ArrowRight className="w-4 h-4" />}
      </button>
    </form>
  );
}

// ------------------------------------------------------------------------------------------------
// Company card — the client's identity, shown only on this token-gated page (§6.2 / §6.3).
// ------------------------------------------------------------------------------------------------

function CompanyCard({ company }: { company: NonNullable<InvitePageData['company']> }) {
  const facts = [
    company.industry,
    company.company_size ? `${company.company_size} employees` : null,
    company.hq_city ? `HQ: ${company.hq_city}` : null,
    company.founded_year ? `Founded ${company.founded_year}` : null,
  ].filter((f): f is string => Boolean(f));
  const website = externalUrl(company.website);

  return (
    <section className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
      <div className="flex items-center gap-3">
        {company.logo_url ? (
          <img
            src={company.logo_url}
            alt=""
            referrerPolicy="no-referrer"
            className="w-12 h-12 rounded-xl object-contain bg-white border border-gray-100"
          />
        ) : (
          <div className="w-12 h-12 rounded-xl bg-white border border-gray-100 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-brand-muted" />
          </div>
        )}
        <div className="min-w-0">
          <p className="font-black text-brand-primary">{company.name}</p>
          {company.tagline && <p className="text-sm text-brand-muted">{company.tagline}</p>}
        </div>
      </div>

      {facts.length > 0 && <p className="mt-3 text-xs text-brand-muted">{facts.join(' · ')}</p>}

      {company.about && (
        <p className="mt-4 text-sm text-brand-primary whitespace-pre-line">{company.about}</p>
      )}

      <BulletList title="Why work here" items={company.why_work_here} />
      <BulletList title="Work environment" items={company.work_environment} />

      {website && (
        <a
          href={website}
          target="_blank"
          // noreferrer: the Referer would otherwise carry this page's URL, token included.
          rel="noopener noreferrer"
          className="mt-4 inline-block text-sm font-bold text-brand-primary hover:text-brand-accent break-all"
        >
          {company.website}
        </a>
      )}
    </section>
  );
}

function BulletList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-4">
      <p className="text-xs font-black uppercase tracking-widest text-brand-muted mb-1.5">{title}</p>
      <ul className="list-disc pl-5 space-y-1 text-sm text-brand-primary">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

/** Only http(s) links are rendered; a bare host gets https://. */
function externalUrl(raw: string | null): string | null {
  if (!raw) return null;
  const value = raw.trim();
  const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(withScheme);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

// ------------------------------------------------------------------------------------------------
// Small pieces
// ------------------------------------------------------------------------------------------------

/** Keeps the token page out of search indexes for as long as it is mounted. */
function useNoIndex(): void {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
}

const PRIMARY_BUTTON =
  'w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-brand-deep transition-all shadow-lg shadow-brand-primary/10 disabled:opacity-50 disabled:cursor-not-allowed';

function PrimaryLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className={PRIMARY_BUTTON}>
      {children}
      <ArrowRight className="w-4 h-4" />
    </Link>
  );
}

function SecondaryLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="block w-full py-4 border border-gray-200 text-brand-primary rounded-2xl font-black text-sm uppercase tracking-widest text-center hover:bg-gray-50 transition-all"
    >
      {children}
    </Link>
  );
}
